import { Capacitor } from '@capacitor/core';

export const REMOTE_API_ORIGIN = 'https://ck-pulse.pages.dev';

export function getApiBase(): string {
  if (import.meta.env.VITE_API_URL) {
    return `${import.meta.env.VITE_API_URL}/api/v1`;
  }
  // When running inside Capacitor mobile app or on file/localhost outside dev
  if (
    Capacitor.isNativePlatform() ||
    (typeof window !== 'undefined' &&
      (window.location.protocol === 'capacitor:' ||
        window.location.protocol === 'file:' ||
        (window.location.hostname === 'localhost' && !import.meta.env.DEV)))
  ) {
    return `${REMOTE_API_ORIGIN}/api/v1`;
  }
  return '/api/v1';
}

const API_BASE = getApiBase();

export class ApiError extends Error {
  code: string;
  details?: unknown;

  constructor(message: string, code: string = 'API_ERROR', details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('pulse_token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (response.status === 200) {
      throw new ApiError(
        'Unable to reach API server. Please check internet connection.',
        'NETWORK_ERROR'
      );
    }
    throw new ApiError(`Request failed with status ${response.status}`, 'HTTP_ERROR');
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data.success) {
    const error = data.error || {};
    throw new ApiError(
      error.message || `Request failed with status ${response.status}`,
      error.code || 'UNKNOWN_ERROR',
      error.details
    );
  }

  return data.data;
}

export const api = {
  // Auth
  auth: {
    login: (credentials: { email: string; password: string }) =>
      request<{ token: string; user: any }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),
    registerOrg: (payload: {
      organizationName: string;
      adminEmail: string;
      adminPassword: string;
      adminFullName: string;
      licenseKey: string;
    }) =>
      request<{ token: string; organization: any; user: any }>('/auth/register-org', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    resetPassword: (payload: { adminEmail: string; licenseKey: string; newPassword: string }) =>
      request<{ token: string; user: any; message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    me: () => request<{ user: any; remainingDays: number | null }>('/auth/me'),
    logout: () =>
      request<{ message: string }>('/auth/logout', {
        method: 'POST',
      }),
  },

  // Super Admin
  superadmin: {
    getTelemetry: () => request<any>('/superadmin/telemetry'),
    getOrganizations: () => request<{ organizations: any[] }>('/superadmin/organizations'),
    suspendOrg: (id: string, reason: string) =>
      request<any>(`/superadmin/organizations/${id}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    reactivateOrg: (id: string) =>
      request<any>(`/superadmin/organizations/${id}/reactivate`, {
        method: 'POST',
      }),
    getLicenses: () => request<{ licenses: any[] }>('/superadmin/licenses'),
    generateLicense: (payload: {
      duration: string;
      plan?: 'TRIAL' | 'STARTER' | 'PRO' | 'ENTERPRISE' | 'CUSTOM' | string;
      maxDevices?: number;
      notes?: string;
      metadata?: any;
    }) =>
      request<{ license: any }>('/superadmin/licenses/generate', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    revokeLicense: (id: string) =>
      request<any>(`/superadmin/licenses/${id}/revoke`, {
        method: 'POST',
      }),
    getAuditLogs: () => request<{ auditLogs: any[] }>('/superadmin/audit'),
  },

  // Customer Admin
  admin: {
    getDashboard: () => request<any>('/admin/dashboard'),
    getSubscription: () =>
      request<{
        subscription: {
          id: string;
          organizationId: string;
          status: string;
          duration: string;
          isLifetime: boolean;
          remainingDays: number;
          plan?: string;
          maxDevices?: number;
          deviceCount?: number;
          keyMasked?: string;
          activatedAt: string;
          expiresAt: string | null;
        } | null;
      }>('/admin/subscription'),
    getUsers: () => request<{ users: any[] }>('/admin/users'),
    createUser: (payload: { email: string; fullName: string; department?: string; role?: string }) =>
      request<{ user: any }>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    updateUser: (id: string, payload: { fullName?: string; department?: string; disabled?: boolean }) =>
      request<{ user: any }>(`/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    deleteUser: (id: string) =>
      request<{ message: string }>(`/admin/users/${id}`, {
        method: 'DELETE',
      }),
    generateEnrollmentToken: (userId: string) =>
      request<{ enrollmentToken: any }>(`/admin/users/${userId}/enrollment-token`, {
        method: 'POST',
      }),
    generateDeviceEnrollmentToken: (userId?: string) =>
      request<{ enrollmentToken: any }>(`/admin/devices/enrollment-token`, {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    getDevices: () => request<{ devices: any[] }>('/admin/devices'),
    updateDevice: (id: string, payload: { deviceName?: string; status?: string; userId?: string | null }) =>
      request<{ device: any }>(`/admin/devices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    revokeDevice: (id: string) =>
      request<{ message: string }>(`/admin/devices/${id}`, {
        method: 'DELETE',
      }),
    getSettings: () => request<{ settings: any }>('/admin/settings'),
    updateSettings: (payload: any) =>
      request<{ settings: any }>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    approveBreak: (deviceId: string) =>
      request<{ message: string; breakStatus: string; breakExpiresAt: string }>(
        `/admin/devices/${deviceId}/break/approve`,
        { method: 'POST' }
      ),
    declineBreak: (deviceId: string) =>
      request<{ message: string; breakStatus: string }>(
        `/admin/devices/${deviceId}/break/decline`,
        { method: 'POST' }
      ),
    getLiveMonitor: () =>
      request<{
        workstations: Array<{
          id: string;
          deviceId: string;
          hostname: string;
          deviceName: string;
          os: string;
          agentVersion: string;
          status: string;
          breakStatus?: string;
          breakExpiresAt?: string | null;
          breakLabel?: string | null;
          breakRequestedType?: string | null;
          breakRequestedDuration?: number;
          lastSeenAt: string;
          lastActivityAt: string | null;
          user: { id: string; fullName: string; email: string; department: string | null } | null;
          activeInactivity: { id: string; startedAt: string; elapsedSeconds: number } | null;
        }>;
        metrics: {
          total: number;
          active: number;
          inactive: number;
          offline: number;
        };
      }>('/admin/live-monitor'),
    getFloorPlan: () =>
      request<{
        floorPlan: {
          gridWidth: number;
          gridHeight: number;
          zones: Array<{
            id: string;
            name: string;
            color: string;
            x: number;
            y: number;
            w: number;
            h: number;
          }>;
          desks: Array<{
            id: string;
            label: string;
            deviceId?: string | null;
            x: number;
            y: number;
            zoneId?: string;
          }>;
        };
        workstations: Array<any>;
        metrics: {
          total: number;
          active: number;
          onBreak: number;
          inactive: number;
          shiftEnded: number;
          offline: number;
        };
      }>('/admin/floor-plan'),
    updateFloorPlan: (floorPlan: any) =>
      request<{ message: string; floorPlan: any }>('/admin/floor-plan', {
        method: 'PUT',
        body: JSON.stringify({ floorPlan }),
      }),
    getActivity: (params?: {
      range?: string;
      startDate?: string;
      endDate?: string;
      userId?: string;
      deviceId?: string;
      status?: string;
    }) => {
      const query = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v) query.append(k, v);
        });
      }
      const qs = query.toString();
      return request<{
        events: Array<any>;
        summary: {
          totalEvents: number;
          totalInactiveSeconds: number;
          longestInactiveSeconds: number;
        };
      }>(`/admin/activity${qs ? `?${qs}` : ''}`);
    },
    exportActivity: async (params?: {
      range?: string;
      startDate?: string;
      endDate?: string;
      userId?: string;
      deviceId?: string;
      status?: string;
    }) => {
      const query = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v) query.append(k, v);
        });
      }
      const qs = query.toString();
      const token = localStorage.getItem('pulse_token');
      const res = await fetch(`${API_BASE}/admin/activity/export${qs ? `?${qs}` : ''}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to export activity CSV');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ck-pulse-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    getReportsAnalytics: (params?: { range?: string; startDate?: string; endDate?: string }) => {
      const query = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v) query.append(k, v);
        });
      }
      const qs = query.toString();
      return request<{
        range: string;
        totalDevices: number;
        totalUsers: number;
        summary: {
          totalEvents: number;
          totalInactiveSeconds: number;
          longestInactiveSeconds: number;
          avgInactiveSeconds: number;
        };
        departmentBreakdown: Array<{
          department: string;
          totalEvents: number;
          totalInactiveSeconds: number;
          activeEmployees: number;
        }>;
        hourlyDistribution: Array<{
          hour: number;
          label: string;
          eventCount: number;
          totalInactiveSeconds: number;
        }>;
        shiftOfflineReport?: {
          isShiftActive: boolean;
          isWorkDay: boolean;
          shiftHoursStart: string;
          shiftHoursEnd: string;
          timezone: string;
          workDays: string;
          totalEnrolled: number;
          offlineInShiftCount: number;
          onlineInShiftCount: number;
          shiftComplianceRate: number;
          devices: Array<{
            id: string;
            deviceId: string;
            deviceName: string;
            hostname: string;
            userName: string;
            userEmail: string;
            userDepartment: string;
            status: string;
            lastSeenAt: string | null;
            offlineMinutes: number;
            minutesSinceLastSeen: number;
            seenTodayInShift: boolean;
            classification: string;
            classificationLabel: string;
          }>;
        };
        recentEvents: Array<any>;
      }>(`/admin/reports/analytics${qs ? `?${qs}` : ''}`);
    },
    getShiftOfflineReport: () => request<any>('/admin/reports/shift-offline'),
    exportShiftOfflineCsv: async () => {
      const token = localStorage.getItem('pulse_token');
      const res = await fetch(`${API_BASE}/admin/reports/shift-offline/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to export shift offline report');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ck-pulse-shift-offline-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    getUserProfile: (id: string) =>
      request<{
        user: {
          id: string;
          fullName: string;
          email: string;
          department: string | null;
          role: string;
          disabled: boolean;
          createdAt: string;
        };
        device: any | null;
        presence: {
          status: string;
          activeInactivity: { id: string; startedAt: string; elapsedSeconds: number } | null;
        };
        todayMetrics: {
          totalInactiveSeconds: number;
          eventCount: number;
          longestInactiveSeconds: number;
        };
        timeline: Array<any>;
      }>(`/admin/users/${id}/profile`),
    getNotificationChannels: () =>
      request<{
        channels: Array<{
          id: string;
          type: 'EMAIL' | 'TELEGRAM' | 'WEBHOOK';
          name: string;
          enabled: boolean;
          minInactivitySeconds: number;
          config: Record<string, unknown>;
          totalDispatched: number;
          createdAt: string;
          updatedAt: string;
        }>;
      }>('/admin/notifications/channels'),
    createNotificationChannel: (payload: {
      type: 'EMAIL' | 'TELEGRAM' | 'WEBHOOK';
      name: string;
      enabled?: boolean;
      minInactivitySeconds?: number;
      config: Record<string, unknown>;
    }) =>
      request<{ channel: any }>('/admin/notifications/channels', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    updateNotificationChannel: (
      id: string,
      payload: {
        name?: string;
        enabled?: boolean;
        minInactivitySeconds?: number;
        config?: Record<string, unknown>;
      }
    ) =>
      request<{ channel: any }>(`/admin/notifications/channels/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    deleteNotificationChannel: (id: string) =>
      request<{ message: string }>(`/admin/notifications/channels/${id}`, {
        method: 'DELETE',
      }),
    testNotificationChannel: (id: string) =>
      request<{ success: boolean; recipient: string; summary: string; error?: string }>(
        `/admin/notifications/channels/${id}/test`,
        { method: 'POST' }
      ),
    getNotificationLogs: () =>
      request<{
        logs: Array<{
          id: string;
          channelId: string | null;
          channelType: string;
          recipient: string;
          status: string;
          summary: string;
          error: string | null;
          createdAt: string;
          channel?: { name: string; type: string } | null;
        }>;
      }>('/admin/notifications/logs'),
    getPushStatus: () =>
      request<{
        isSubscribed: boolean;
        activeDevices: number;
        subscriptions: Array<any>;
      }>('/admin/notifications/push/status'),
    subscribePush: (payload: { endpoint: string; p256dh: string; auth: string; userAgent?: string }) =>
      request<{ message: string }>('/admin/notifications/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    unsubscribePush: (endpoint: string) =>
      request<{ message: string }>('/admin/notifications/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }),
    testPush: () =>
      request<{ message: string; deliveredCount: number }>('/admin/notifications/push/test', {
        method: 'POST',
      }),
    ringDevice: (id: string) =>
      request<{ message: string }>(`/admin/devices/${id}/ring`, {
        method: 'POST',
      }),
    getLatestAlert: () =>
      request<{ title: string; body: string; url?: string }>('/admin/notifications/latest-alert'),
    getSessions: () =>
      request<{
        sessions: Array<{
          id: string;
          ipAddress: string;
          userAgent: string;
          lastActiveAt: string;
          createdAt: string;
          isCurrent: boolean;
        }>;
      }>('/admin/sessions'),
    revokeSession: (id: string) =>
      request<{ message: string }>(`/admin/sessions/${id}`, {
        method: 'DELETE',
      }),
    revokeOtherSessions: () =>
      request<{ message: string }>('/admin/sessions', {
        method: 'DELETE',
      }),
    getAuditLogs: () =>
      request<{
        auditLogs: Array<{
          id: string;
          actorEmail: string;
          action: string;
          ipAddress: string;
          timestamp: string;
          metadata: Record<string, any>;
        }>;
      }>('/admin/audit-logs'),
  },
};
